import React, { useState, useEffect, useCallback } from 'react';
import ContentHeader from '../components/ContentHeader';
import { superAdminAPI } from '../services/api';
import './SuperAdmin.css';

function formatSnapshotUserLine(entry) {
  const staff = [entry.staff_first_name, entry.staff_last_name].filter(Boolean).join(' ').trim();
  if (staff) return staff;
  return entry.email || entry.username || (entry.user_id != null ? `User #${entry.user_id}` : '—');
}

function formatSnapshotUserSecondary(entry) {
  const staff = [entry.staff_first_name, entry.staff_last_name].filter(Boolean).join(' ').trim();
  const email = entry.email || entry.username || '';
  if (staff && email) return email;
  return null;
}

const BILLING_MODELS = [
  { value: 'hourly_consulting', label: 'Hourly / consulting' },
  { value: 'monthly_seats', label: 'Monthly seats (SaaS)' },
  { value: 'custom', label: 'Custom' },
  { value: 'internal', label: 'Internal / non-production' },
  { value: 'none', label: 'None' },
];

export function SuperAdminBillingTab() {
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [users, setUsers] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [periodInput, setPeriodInput] = useState('');
  const [saveMsg, setSaveMsg] = useState(null);
  const [expandedSnapshotId, setExpandedSnapshotId] = useState(null);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await superAdminAPI.getBillingSummary();
      setSummary(res.data?.data || []);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.error || e.message || 'Failed to load billing summary');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const loadDetail = async (tenantId) => {
    setSelectedId(tenantId);
    setDetailLoading(true);
    setSaveMsg(null);
    setError(null);
    setPeriodInput('');
    try {
      const [pRes, uRes, sRes] = await Promise.all([
        superAdminAPI.getTenantBillingProfile(tenantId),
        superAdminAPI.getTenantBillingUsers(tenantId),
        superAdminAPI.getTenantBillingSnapshots(tenantId),
      ]);
      setProfile(pRes.data?.data || null);
      setUsers(uRes.data?.data || []);
      setSnapshots(sRes.data?.data || []);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.error || 'Failed to load tenant billing detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const saveProfile = async () => {
    if (!selectedId || !profile) return;
    setSaveMsg(null);
    try {
      await superAdminAPI.patchTenantBillingProfile(selectedId, {
        billing_model: profile.billing_model,
        qbo_customer_id: profile.qbo_customer_id,
        billing_notes: profile.billing_notes,
      });
      setSaveMsg('Profile saved.');
      loadSummary();
    } catch (e) {
      setSaveMsg(e.response?.data?.error || 'Save failed');
    }
  };

  const toggleBillable = async (userId, nextVal) => {
    if (!selectedId) return;
    try {
      await superAdminAPI.patchTenantUserBilling(selectedId, userId, { is_billable: nextVal });
      const uRes = await superAdminAPI.getTenantBillingUsers(selectedId);
      setUsers(uRes.data?.data || []);
      loadSummary();
    } catch (e) {
      setError(e.response?.data?.error || 'Update failed');
    }
  };

  const recordSnapshot = async () => {
    if (!selectedId || !periodInput.trim()) return;
    setSaveMsg(null);
    try {
      await superAdminAPI.createTenantBillingSnapshot(selectedId, {
        billing_period: periodInput.trim(),
        source: 'manual',
      });
      const sRes = await superAdminAPI.getTenantBillingSnapshots(selectedId);
      setSnapshots(sRes.data?.data || []);
      setSaveMsg(`Snapshot saved for ${periodInput.trim()}.`);
      loadSummary();
    } catch (e) {
      setSaveMsg(e.response?.data?.error || 'Snapshot failed');
    }
  };

  const selected = summary.find((t) => t.tenant_id === selectedId);

  return (
    <div className="super-admin-tab-content">
      <ContentHeader
        title="Billing"
        titleHint="Per-tenant commercial flags, QuickBooks notes, and monthly snapshots."
      />

      {error && (
        <div className="alert alert-error" style={{ marginBottom: 12 }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="super-admin-header">
        <h2 className="super-admin-section-title">All tenants</h2>
        <p className="super-admin-subtitle">
          Billable seats = enabled users with at least one enabled role in the tenant, and marked billable. Use detail to exclude internal/test logins.
        </p>
      </div>

      {loading ? (
        <p>Loading…</p>
      ) : (
        <div className="super-admin-table">
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Billable seats</th>
                <th>Model</th>
                <th>QBO customer id</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {summary.map((row) => (
                <tr key={row.tenant_id}>
                  <td>{row.code}</td>
                  <td>{row.name}</td>
                  <td>{row.billable_user_count}</td>
                  <td>{row.billing_model}</td>
                  <td>{row.qbo_customer_id || '—'}</td>
                  <td>
                    <button type="button" className="btn btn-sm btn-secondary" onClick={() => loadDetail(row.tenant_id)}>
                      Detail
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedId != null && (
        <div style={{ marginTop: 28, borderTop: '1px solid #dee2e6', paddingTop: 20 }}>
          <h2 className="super-admin-section-title">
            {selected ? `${selected.code} — ${selected.name}` : 'Tenant'}
          </h2>
          {detailLoading ? (
            <p>Loading detail…</p>
          ) : (
            <>
              {saveMsg && <p style={{ color: saveMsg.includes('failed') ? '#c00' : '#276749' }}>{saveMsg}</p>}
              <div style={{ display: 'grid', gap: 12, maxWidth: 560, marginBottom: 20 }}>
                <label>
                  Billing model
                  <select
                    className="form-control"
                    value={profile?.billing_model || 'custom'}
                    onChange={(e) => setProfile((p) => ({ ...p, billing_model: e.target.value }))}
                  >
                    {BILLING_MODELS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  QuickBooks customer id
                  <input
                    className="form-control"
                    value={profile?.qbo_customer_id || ''}
                    onChange={(e) => setProfile((p) => ({ ...p, qbo_customer_id: e.target.value }))}
                    placeholder="Optional"
                  />
                </label>
                <label>
                  Notes
                  <textarea
                    className="form-control"
                    rows={3}
                    value={profile?.billing_notes || ''}
                    onChange={(e) => setProfile((p) => ({ ...p, billing_notes: e.target.value }))}
                    placeholder="Contract notes, exceptions…"
                  />
                </label>
                <button type="button" className="btn btn-primary" onClick={saveProfile}>
                  Save profile
                </button>
              </div>

              <h3 className="super-admin-section-title" style={{ fontSize: '1rem' }}>
                Users (billable flag)
              </h3>
              <div className="super-admin-table">
                <table>
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Staff</th>
                      <th>User enabled</th>
                      <th>Has role</th>
                      <th>Billable</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.user_id}>
                        <td>{u.email || u.username}</td>
                        <td>
                          {[u.staff_first_name, u.staff_last_name].filter(Boolean).join(' ') || '—'}
                        </td>
                        <td>{u.user_enabled ? 'Yes' : 'No'}</td>
                        <td>{u.has_enabled_membership ? 'Yes' : 'No'}</td>
                        <td>
                          <input
                            type="checkbox"
                            checked={u.is_billable}
                            onChange={(e) => toggleBillable(u.user_id, e.target.checked)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h3 className="super-admin-section-title" style={{ fontSize: '1rem', marginTop: 24 }}>
                Monthly snapshots
              </h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                <input
                  className="form-control"
                  style={{ width: 140 }}
                  placeholder="YYYY-MM"
                  value={periodInput}
                  onChange={(e) => setPeriodInput(e.target.value)}
                />
                <button type="button" className="btn btn-secondary" onClick={recordSnapshot}>
                  Record / refresh snapshot
                </button>
              </div>
              <div className="super-admin-table">
                <table>
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th>Billable count</th>
                      <th>Source</th>
                      <th>Computed</th>
                      <th>Roster</th>
                    </tr>
                  </thead>
                  <tbody>
                    {snapshots.map((s) => {
                      const roster = Array.isArray(s.billable_users_snapshot) ? s.billable_users_snapshot : null;
                      const open = expandedSnapshotId === s.id;
                      return (
                        <React.Fragment key={s.id}>
                          <tr>
                            <td>{s.billing_period}</td>
                            <td>{s.billable_user_count}</td>
                            <td>{s.source}</td>
                            <td>{s.computed_at ? new Date(s.computed_at).toLocaleString() : '—'}</td>
                            <td>
                              {roster && roster.length > 0 ? (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-secondary"
                                  onClick={() => setExpandedSnapshotId(open ? null : s.id)}
                                >
                                  {open ? 'Hide' : 'Show'} ({roster.length})
                                </button>
                              ) : (
                                <span className="super-admin-subtitle" style={{ fontSize: 12 }}>
                                  {roster && roster.length === 0 ? 'Empty' : '—'}
                                </span>
                              )}
                            </td>
                          </tr>
                          {open && roster && roster.length > 0 && (
                            <tr className="super-admin-snapshot-roster-row">
                              <td colSpan={5}>
                                <ul className="super-admin-snapshot-roster-list">
                                  {roster.map((entry) => {
                                    const sec = formatSnapshotUserSecondary(entry);
                                    return (
                                      <li key={entry.user_id}>
                                        <strong>{formatSnapshotUserLine(entry)}</strong>
                                        {sec && <span className="super-admin-snapshot-roster-secondary"> {sec}</span>}
                                      </li>
                                    );
                                  })}
                                </ul>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default SuperAdminBillingTab;
